// src/components/PatientUpdateModal.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import CustomModal from "./CustomModal";
import PatientUpdateFormBody from "./PatientUpdateFormBody";
import CustomButton from "./CustomButton";
import {
  FIELDS,
  SECTION_GROUPS,
  labelOf,
  typeOf,
  buildOptionsFromFields,
} from "../schema/patientSchema";

const isEmpty = (v) => v === "" || v === null || v === undefined;

export default function PatientUpdateModal({
  open,
  initialValues,
  options,
  onClose,
  onSave,
}) {
  const [formValues, setFormValues] = useState(() => initialValues ?? {});
  const [errors, setErrors] = useState([]);

  const includeEdss = false;

  const visibleKeys = useMemo(() => {
    const keys = [
      ...SECTION_GROUPS.personal,
      ...SECTION_GROUPS.history,
      ...SECTION_GROUPS.lab,
    ];
    return includeEdss ? keys : keys.filter((k) => k !== "edss_score");
  }, [includeEdss]);

  const optMap = useMemo(() => {
    return options ?? buildOptionsFromFields();
  }, [options]);

  useEffect(() => {
    if (!open) return;
    setFormValues(initialValues ?? {});
    setErrors([]);
  }, [open, initialValues]);

  const onInputChange = useCallback((id, value) => {
    setFormValues((prev) => {
      if (prev[id] === value) return prev; // 無駄な再描画防止
      return { ...prev, [id]: value };
    });
  }, []);

  const onSelectChange = useCallback((id, value) => {
    setFormValues((prev) => {
      if (prev[id] === value) return prev;
      return { ...prev, [id]: value };
    });
  }, []);

  const validate = useCallback(() => {
    const errs = [];

    for (const f of FIELDS) {
      if (!f.required) continue;
      if (!visibleKeys.includes(f.key)) continue;

      const v = formValues[f.key];
      const t = typeOf(f.key);

      if (t === "enum") {
        const list = optMap[f.key] || [];
        const isValidEnum = list.some((it) => it.id === v);
        if (isEmpty(v) || !isValidEnum) {
          errs.push(`${labelOf(f.key)}を選択してください。`);
        }
      } else {
        if (isEmpty(v)) {
          errs.push(`${labelOf(f.key)}は必須です。`);
        }
      }
    }

    setErrors(errs);
    return errs.length === 0;
  }, [formValues, optMap, visibleKeys]);

  const handleSave = useCallback(() => {
    if (!validate()) return;
    onSave(formValues);
  }, [validate, onSave, formValues]);

  return (
    <CustomModal
      open={open}
      title="患者基本情報の編集"
      onClose={onClose}
      size="xl"
      closeOnOverlay={false}
      footer={
        <>
          <CustomButton kind="secondary" onClick={onClose}>
            キャンセル
          </CustomButton>
          <CustomButton kind="primary" onClick={handleSave}>
            保存
          </CustomButton>
        </>
      }
    >
      {errors.length > 0 && (
        <div
          style={{
            marginBottom: "1rem",
            background: "#fff",
            border: "1px solid #fa4d56",
            borderRadius: 6,
            color: "#750e13",
            padding: "0.75rem 1rem",
          }}
          aria-live="assertive"
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            入力内容を確認してください
          </div>
          {errors.map((e, i) => (
            <div key={i}>・{e}</div>
          ))}
        </div>
      )}

      <PatientUpdateFormBody
        formValues={formValues}
        onInputChange={onInputChange}
        onSelectChange={onSelectChange}
        options={optMap}
        includeEdss={includeEdss}
      />
    </CustomModal>
  );
}
